import Papa from "papaparse";
import { useCallback, useEffect, useState } from "react";

import { TableColumnType } from "@/components/@types";
import { FileDropzone } from "@/components/molecules/FileDropzone";
import { CSVColumnMatcher } from "@/components/organisms/CSV/CSVColumnMatcher";
import { Button } from "@/components/ui/button";
import { Typography } from "@/components/ui/typography";

export type HeaderIndicesType = {
  [key: string]: number;
};

export function CSV({
  onSubmit,
  expectedHeaders = [],
}: {
  onSubmit: (
    file: File,
    headerIndices: HeaderIndicesType,
    headerRow: number,
    rows: Record<string, any>[],
  ) => void;
  expectedHeaders: TableColumnType[];
}) {
  const [activeStep, setActiveStep] = useState(0);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [parsedFileData, setParsedFileData] = useState(null);
  const [uploadError, setUploadError] = useState(false);

  // Assume headers are in the first row of the file
  const fileHeaders: string[] = Object.values(parsedFileData?.data?.[0] || []);

  const initialHeaderIndices = {};
  Object.keys(expectedHeaders || {}).forEach((key, index) => {
    if (fileHeaders[index]) {
      initialHeaderIndices[key] = index;
    } else {
      initialHeaderIndices[key] = null;
    }
  });

  const [headerIndices, setHeaderIndices] =
    useState<HeaderIndicesType>(initialHeaderIndices);

  useEffect(() => {
    setHeaderIndices(initialHeaderIndices);
  }, []);

  const onDrop = useCallback(
    (files: File[]) => {
      setParsedFileData(null);
      setUploadError(false);
      const file = files[0];
      if (file.type !== "text/csv") {
        setUploadError(true);
        return;
      }
      setCsvFile(file);
      Papa.parse(file, {
        preview: 20,
        skipEmptyLines: true,
        error: () => {
          setUploadError(true);
        },
        complete: (
          data: {
            errors: string[];
            data: string[];
            meta: unknown;
          } /* , file */,
        ) => {
          if (data.errors.length > 0 || data.data.length > 200) {
            setUploadError(true);
          } else {
            setParsedFileData(data);
            setActiveStep(1);
          }
        },
      });
    },
    [csvFile],
  );

  // Returns true if all required columns are matched
  const getValidHeaderIndices = () => {
    return true;
  };

  const mapRowsToExpectedFormat = useCallback(
    (data: any[]): Record<string, any>[] => {
      // Skip the header row
      const dataRows = data.slice(1);

      return dataRows.map((row) => {
        const mappedRow: Record<string, any> = {};

        // For each expected header, map the value from the correct column index
        Object.entries(headerIndices).forEach(
          ([expectedHeader, columnIndex]) => {
            if (columnIndex !== null) {
              mappedRow[expectedHeader] = row[columnIndex];
            }
          },
        );

        return mappedRow;
      });
    },
    [headerIndices],
  );

  return (
    <div className="min-w-0 w-full">
      <div className="flex min-w-0 flex-wrap flex-col">
        {/* Upload file */}
        {activeStep === 0 && (
          <>
            <div className="min-w-0 mt-0">
              <FileDropzone onDrop={onDrop} />
            </div>
            {uploadError && (
              <div className="min-w-0 mt-4 mb-4">
                <Typography className="text-destructive">
                  Unable to upload file. Please ensure the file is a CSV, less
                  than 500 lines, and try again. If issues persist contact
                  support.
                </Typography>
              </div>
            )}
          </>
        )}
        {activeStep === 1 && (
          <>
            <div className="min-w-0">
              <Typography level="body-sm" className="text-muted-foreground/80">
                Please match the columns in your CSV file to the expected
                columns.
              </Typography>
            </div>
            <div className="min-w-0 w-full mt-2">
              <CSVColumnMatcher
                expectedHeaders={expectedHeaders}
                fileHeaders={fileHeaders}
                headerIndicesState={[headerIndices, setHeaderIndices]}
              />
            </div>
            <Button
              variant="ghost"
              disabled={Object.keys(headerIndices).length === 0}
              onClick={() => {
                const isValid = getValidHeaderIndices();
                if (!isValid) {
                  // TODO: Show error message
                  return;
                } else {
                  const mappedRows = mapRowsToExpectedFormat(
                    parsedFileData.data,
                  );
                  onSubmit(csvFile, headerIndices, 1, mappedRows);
                }
              }}
            >
              Submit
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
